import { ForecastTimePeriod } from "./useForecastData";

type ForecastPositionSource = {
  forecastChartData: Array<{
    month: string;
    target?: number;
    total_minus_risk?: number;
    expected_close_v2?: number | null;
    won?: number;
  }>;
};

type V2ForecastPositionRow = ForecastPositionSource["forecastChartData"][number] & {
  expected_close_v2?: number | null;
  is_context?: boolean;
};

export type StateOfSalesV2ForecastPosition = {
  expectedFromOpenDeals: number;
  forecast: number;
  future: boolean;
  gapToTarget: number;
  historical: boolean;
  target: number;
  won: number;
};

export function getStateOfSalesV2ForecastPosition(
  forecast: ForecastPositionSource,
  period: ForecastTimePeriod,
  accumulated: boolean,
  v2ChartData?: V2ForecastPositionRow[],
): StateOfSalesV2ForecastPosition {
  const hasLeadingContextMonth = period === "this_year" || period === "next_6_months";
  const leadingContextRow = hasLeadingContextMonth
    ? forecast.forecastChartData.at(0)
    : undefined;
  const periodRows = hasLeadingContextMonth
    ? forecast.forecastChartData.slice(1)
    : forecast.forecastChartData;
  const won = periodRows.reduce(
    (sum, row) => sum + Number(row.won ?? 0),
    0,
  );
  const calculatedExpectedToClose = accumulated
    ? Number(periodRows.at(-1)?.total_minus_risk ?? 0)
      - Number(leadingContextRow?.total_minus_risk ?? 0)
    : periodRows.reduce(
      (sum, row) => sum + Number(row.total_minus_risk ?? 0),
      0,
    );
  const target = accumulated
    ? Number(periodRows.at(-1)?.target ?? 0)
      - Number(leadingContextRow?.target ?? 0)
    : periodRows.reduce(
      (sum, row) => sum + Number(row.target ?? 0),
      0,
    );
  const historical = period === "prev_quarter";
  const future = period === "next_quarter" || period === "next_month";
  const selectedMonths = new Set(periodRows.map(row => row.month));
  let v2ForecastValue: number | null = null;
  if (!historical && v2ChartData?.some(row => row.expected_close_v2 !== null)) {
    let previous = 0;
    v2ForecastValue = v2ChartData.reduce((sum, row) => {
      const current = Number(row.expected_close_v2 ?? 0);
      const monthly = accumulated
        ? row.is_context
          ? current
          : Math.max(0, current - previous)
        : current;
      previous = row.is_context ? 0 : current;
      return selectedMonths.has(row.month) ? sum + monthly : sum;
    }, 0);
  }
  const forecastValue = historical ? won : v2ForecastValue ?? calculatedExpectedToClose;
  const expectedFromOpenDeals = historical ? 0 : Math.max(0, forecastValue - won);

  return {
    expectedFromOpenDeals,
    forecast: forecastValue,
    future,
    gapToTarget: forecastValue - target,
    historical,
    target,
    won,
  };
}
